/**
 * ResourceFilterPanel Component (T080 - US3)
 * Comprehensive filter panel for resource content management
 * 
 * Supports filters: search, status, type, category, featured flags
 * 
 * TODO: STATUS FILTER LIMITATION
 * The status filter is currently limited because the Resource table
 * does NOT have a 'status' field in the database. Status is derived
 * in the frontend from publishedAt and deletedAt fields.
 * 
 * Once the database migration adds the status column:
 * 1. Backend can filter by status directly
 * 2. This filter will work properly for all status values
 * 3. Remove this TODO comment
 */

import { Search, X, Filter } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import type { ResourceStatus } from '../types/admin-resource.type';

type FilterOption = {
  value: string;
  label: string;
};

type ResourceFilterPanelProps = {
  // Search
  searchValue?: string;
  onSearchChange: (value: string) => void;
  
  // Status filter
  statusValue?: ResourceStatus | '';
  onStatusChange: (value: ResourceStatus | '') => void;
  
  // Type filter
  typeValue?: string;
  onTypeChange: (value: string) => void;
  typeOptions?: FilterOption[];
  
  // Category filter
  categoryValue?: string;
  onCategoryChange: (value: string) => void;
  categoryOptions?: FilterOption[];
  
  // Featured filter
  isFeaturedValue?: boolean | undefined;
  onIsFeaturedChange: (value: boolean | undefined) => void;
  
  // UI customization
  searchPlaceholder?: string;
  showAdvancedFilters?: boolean;
};

const STATUS_OPTIONS: FilterOption[] = [
  { value: '', label: 'All Status' },
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' },
];

const FEATURED_OPTIONS: FilterOption[] = [
  { value: '', label: 'All Resources' },
  { value: 'true', label: 'Featured Only' },
  { value: 'false', label: 'Non-Featured' },
];

/**
 * Filter panel component for resource list with comprehensive filters
 * 
 * Features:
 * - Debounced search input (300ms)
 * - Clear search button
 * - Status, type, category dropdowns
 * - Featured filter
 * - Visual feedback for active filters
 * - Filter count indicator
 */
export function ResourceFilterPanel({
  searchValue = '',
  onSearchChange,
  statusValue = '',
  onStatusChange,
  typeValue = '',
  onTypeChange,
  typeOptions = [],
  categoryValue = '',
  onCategoryChange,
  categoryOptions = [],
  isFeaturedValue,
  onIsFeaturedChange,
  searchPlaceholder = 'Search resources by title...',
  showAdvancedFilters = true,
}: ResourceFilterPanelProps) {
  const [localSearch, setLocalSearch] = useState(searchValue);
  const [showFilters, setShowFilters] = useState(false);
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;

  useEffect(() => {
    setLocalSearch(searchValue);
  }, [searchValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== searchValue) {
        if (localSearch.length >= 2 || localSearch.length === 0) {
          onSearchChangeRef.current(localSearch);
        }
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearch, searchValue]);

  const handleClearSearch = () => {
    setLocalSearch('');
    onSearchChange('');
  };

  const handleFeaturedChange = (value: string) => {
    if (value === '') {
      onIsFeaturedChange(undefined);
    } else {
      onIsFeaturedChange(value === 'true');
    }
  };

  const getFeaturedValue = () => {
    if (isFeaturedValue === undefined) return '';
    return isFeaturedValue ? 'true' : 'false';
  };

  // Count active filters
  const activeFilterCount = [
    searchValue,
    statusValue,
    typeValue,
    categoryValue,
    isFeaturedValue !== undefined,
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0;

  return (
    <div className="bg-white border-b border-gray-200">
      {/* Primary filters row */}
      <div className="px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Search input */}
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent"
            />
            {localSearch && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-700 transition-colors"
                aria-label="Clear search"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>

          {/* Status filter */}
          <select
            value={statusValue}
            onChange={(e) => onStatusChange(e.target.value as ResourceStatus | '')}
            className={`px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent min-w-[140px] ${
              statusValue
                ? 'border-[#026857] bg-[#026857]/5 font-medium'
                : 'border-gray-300'
            }`}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Advanced filters toggle */}
          {showAdvancedFilters && (
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-2 border rounded-md transition-colors ${
                showFilters || activeFilterCount > 2
                  ? 'border-[#026857] bg-[#026857]/5 text-[#026857]'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              <span className="text-sm font-medium">Filters</span>
              {activeFilterCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold text-white bg-[#026857] rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Advanced filters row (collapsible) */}
      {showAdvancedFilters && showFilters && (
        <div className="px-4 pb-3">
          <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
            {/* Type filter */}
            {typeOptions.length > 0 && (
              <select
                value={typeValue}
                onChange={(e) => onTypeChange(e.target.value)}
                className={`px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent min-w-[140px] ${
                  typeValue
                    ? 'border-[#026857] bg-[#026857]/5 font-medium'
                    : 'border-gray-300'
                }`}
              >
                <option value="">All Types</option>
                {typeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}

            {/* Category filter */}
            {categoryOptions.length > 0 && (
              <select
                value={categoryValue}
                onChange={(e) => onCategoryChange(e.target.value)}
                className={`px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent min-w-[160px] ${
                  categoryValue
                    ? 'border-[#026857] bg-[#026857]/5 font-medium'
                    : 'border-gray-300'
                }`}
              >
                <option value="">All Categories</option>
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}

            {/* Featured filter */}
            <select
              value={getFeaturedValue()}
              onChange={(e) => handleFeaturedChange(e.target.value)}
              className={`px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent min-w-[160px] ${
                isFeaturedValue !== undefined
                  ? 'border-[#026857] bg-[#026857]/5 font-medium'
                  : 'border-gray-300'
              }`}
            >
              {FEATURED_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Clear all filters */}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={() => {
                  setLocalSearch('');
                  onSearchChange('');
                  onStatusChange('');
                  onTypeChange('');
                  onCategoryChange('');
                  onIsFeaturedChange(undefined);
                }}
                className="ml-auto text-sm text-gray-600 hover:text-gray-900 font-medium"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
